import { memo, type ReactNode } from 'react';

import { XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Portal } from '@air/primitive-portal';
import { VisuallyHidden } from '@air/primitive-visually-hidden';

export const CLOSE_BOARD_CUSTOM_FIELD_PANEL_BUTTON = 'CLOSE_BOARD_CUSTOM_FIELD_PANEL_BUTTON';

export type CustomFieldsPanelProps = {
  children: ReactNode;
  onClose: () => void;
  breadcrumbs?: ReactNode;
  footer?: ReactNode;
  title?: string;
};

export const CustomFieldsPanel = memo(({ children, breadcrumbs, footer, onClose, title }: CustomFieldsPanelProps) => {
  return (
    <Portal>
      <button
        className="fixed inset-0 bg-black/60"
        data-testid="CUSTOM_FIELDS_PANEL_OVERLAY"
        onClick={onClose}
        type="button"
      >
        <VisuallyHidden>Close</VisuallyHidden>
      </button>
      <div
        className="fixed inset-y-0 right-0 flex w-[396px] flex-col overflow-hidden ads:bg-raised bg-surface-1"
        data-disableselect="true"
        data-testid="CUSTOM_FIELDS_PANEL"
      >
        <header className="flex shrink-0 flex-col gap-2 border-b ads:border-b-ads-gray-5 border-b-gray-5 px-6 py-4">
          {!!title && <h1 className="order-2 font-semibold ads:text-primary text-20 text-gray-12">{title}</h1>}
          <div className="order-1 flex items-center justify-between">
            <div>{breadcrumbs}</div>
            <IconButton
              appearance="ghost"
              className="shrink-0"
              color="gray"
              data-testid={CLOSE_BOARD_CUSTOM_FIELD_PANEL_BUTTON}
              icon={XIcon}
              label="Close"
              onClick={onClose}
            />
          </div>
        </header>

        <div className="h-screen grow overflow-auto p-6">{children}</div>

        {!!footer && (
          <footer className="shrink-0 border-t ads:border-t-ads-gray-5 border-t-gray-5 px-6 py-4">{footer}</footer>
        )}
      </div>
    </Portal>
  );
});

CustomFieldsPanel.displayName = 'CustomFieldsPanel';
